/* =============================================================================
   CORE / BRANDING — getBrandLogoUrl + Asset-Defaults (Phase 31, Session 29)
   =============================================================================
   - DEFAULT_LOGO_URL = 'assets/logo.png' (Repo-Asset, immer verfügbar)
   - getBrandLogoUrl(): liest aus _state.restaurant.settings.brand.logoUrl
     (Storage-Internal-State). Falls null/leer: DEFAULT_LOGO_URL.
   - getBrandLogoSrc(): wie oben + Cache-Bust via logoUploadedAt-Timestamp,
     damit der Browser ein neu hochgeladenes Logo nicht aus dem Cache rendert.
   - getBrandFaviconUrl(size): liefert das passende Favicon (32 oder 192).
     Verwendet IMMER das Default-Asset — Favicons müssen synchron beim
     <head>-Parsing verfügbar sein, deshalb kein dynamischer Override.

   Single-Source-of-Truth für alle sechs Logo-Touchpoints (S40-Update —
   reservierung.html entfernt):
     1. Customer-Form Header (index.html)
     2. Admin-Cockpit Header (index.html)
     3. Bon-Receipt (orders.js + receipt.js)
     4. Z-Bon-Receipt (day-close.js)
     5. E-Mail-Templates (Base64, core/email-templates.js)
     6. Favicon (index.html)
     7. Verfahrensdoku (docs/gobd-verfahrensdokumentation.md, statisch)

   Pattern analog zu utils/helpers.js → getRestaurantTimezone(): defensiver
   Read mit DEFAULT-Fallback, niemals throw.
============================================================================ */

export const DEFAULT_LOGO_URL = 'assets/logo.png';
export const DEFAULT_LOGO_SVG_URL = 'assets/logo.svg';
export const DEFAULT_FAVICON_32_URL = 'assets/favicon-32.png';
export const DEFAULT_FAVICON_192_URL = 'assets/favicon-192.png';

/**
 * S58 Block I.5 — Logo-URL-Normalisierung (Hard-Way).
 *
 * Wandelt eine relative URL (z.B. "assets/logo.png" oder "/assets/logo.png")
 * in eine absolute URL um, sodass externe Mail-Clients sie auflösen können.
 * Behandelt:
 *   - Bereits absolute http(s)://… und data:… URLs → unverändert zurück.
 *   - Leere/ungültige URLs → leerer String (Caller entscheidet über Fallback).
 *   - Relative URLs → `<baseUrl>/<url>` mit korrektem Slash-Handling.
 *
 * Auslöser S57: Ein Tenant hatte `restaurants.settings.brand.logoUrl` als
 * relative URL gespeichert ("assets/logo.png" statt der absoluten Storage-URL).
 * Customer-Mails kamen ohne Logo durch, weil Mail-Clients relative URLs nicht
 * gegen den ursprünglichen Sender-Origin auflösen. Quick-Fix war Manual-SQL,
 * Hard-Way-Fix ist diese Helper-Funktion + Anwendung in den Render-Pfaden.
 *
 * @param {string} url        — gespeicherte Logo-URL (kann relativ oder absolut sein)
 * @param {string} [baseUrl]  — optional explizite Base (z.B. `settings.publicUrl`);
 *                              Default = window.location.origin im Browser
 * @returns {string} absolute URL oder leerer String
 */
export function normalizeLogoUrl(url, baseUrl) {
  if (typeof url !== 'string' || !url.trim()) return '';
  const trimmed = url.trim();
  // Bereits absolut?
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^data:/i.test(trimmed))       return trimmed;
  // Base bestimmen — Caller-Override > window.location.origin.
  let base = '';
  if (typeof baseUrl === 'string' && baseUrl.trim()) {
    base = baseUrl.trim();
  } else if (typeof window !== 'undefined' && window.location && window.location.origin
             && window.location.origin !== 'null') {
    base = window.location.origin;
  }
  if (!base) return trimmed; // Letzter Fallback: relative URL bleibt — besser als nichts.
  // Slash-Normalisierung: base ohne trailing-slash, url ohne leading-slash.
  const cleanBase = base.replace(/\/+$/, '');
  const cleanPath = trimmed.replace(/^\/+/, '');
  return cleanBase + '/' + cleanPath;
}

/**
 * S58 Block I.5 — Erkennt eine "verdächtig relative" Logo-URL.
 * Wird vom Settings-UI genutzt, um eine Warnung anzuzeigen.
 */
export function isRelativeLogoUrl(url) {
  if (typeof url !== 'string' || !url.trim()) return false;
  const t = url.trim();
  if (/^https?:\/\//i.test(t)) return false;
  if (/^data:/i.test(t))       return false;
  return true;
}

/** Liest die konfigurierte Logo-URL aus den Restaurant-Settings.
 *  Fallback: DEFAULT_LOGO_URL (Repo-Asset assets/logo.png).
 *
 *  - Wenn `settings.brand.logoUrl` ein nicht-leerer String ist → Storage-URL.
 *  - Wenn `settings.brand.logoUrl` null/undefined/'' → DEFAULT_LOGO_URL.
 *  - Wenn Storage noch nicht initialisiert ist (z.B. beim ersten Render
 *    vor Auth) → DEFAULT_LOGO_URL.
 *
 *  Niemals throw — Branding ist ein UX-Detail, kein App-Blocker.
 */
export function getBrandLogoUrl() {
  try {
    if (typeof window !== 'undefined' && window.App && window.App.storage) {
      const s = window.App.storage._getInternalState
        && window.App.storage._getInternalState();
      const url = s && s.settings && s.settings.brand && s.settings.brand.logoUrl;
      if (typeof url === 'string' && url.trim()) return url.trim();
    }
  } catch (_) { /* defensive — Storage noch nicht da */ }
  return DEFAULT_LOGO_URL;
}

/** Wie getBrandLogoUrl(), aber mit Cache-Bust-Query-Param.
 *
 *  Wenn ein Custom-Logo hochgeladen wird, schreibt das Settings-UI
 *  einen neuen `logoUploadedAt`-Timestamp. Wir hängen diesen als
 *  `?v=<timestamp>` an die URL, damit der Browser-Cache umgangen wird.
 *
 *  Default-Asset (assets/logo.png) bekommt keinen Cache-Bust — dort
 *  greift normales Browser-Caching via ETags/Last-Modified.
 */
export function getBrandLogoSrc() {
  const url = getBrandLogoUrl();
  if (url === DEFAULT_LOGO_URL) return url;

  try {
    if (typeof window !== 'undefined' && window.App && window.App.storage) {
      const s = window.App.storage._getInternalState
        && window.App.storage._getInternalState();
      const uploadedAt = s && s.settings && s.settings.brand && s.settings.brand.logoUploadedAt;
      if (typeof uploadedAt === 'string' && uploadedAt) {
        const sep = url.includes('?') ? '&' : '?';
        return `${url}${sep}v=${encodeURIComponent(uploadedAt)}`;
      }
    }
  } catch (_) { /* defensive */ }

  return url;
}

/** Favicon-URL für eine bestimmte Größe (32 oder 192).
 *  Ignoriert Custom-Logo-Uploads — Favicons sind statisch im <head>
 *  referenziert und müssen beim ersten Parse synchron verfügbar sein. */
export function getBrandFaviconUrl(size) {
  return size >= 96 ? DEFAULT_FAVICON_192_URL : DEFAULT_FAVICON_32_URL;
}

/** DOM-Apply — setzt das aktuelle Brand-Logo auf alle `<img data-brand-logo>`
 *  Elemente im DOM. Wird beim App-Boot (nach State-Load) und nach jeder
 *  Logo-Änderung (Settings-UI) aufgerufen. Pendant zu `applyBrandTheme()`
 *  aus admin/settings.js (Farben → CSS-Variablen).
 *
 *  Non-destruktiv: setzt nur das `src`-Attribut. Keine HTML-Manipulation.
 *  Idempotent: mehrfacher Aufruf macht keinen Unterschied.
 *
 *  S48 Multi-Tenant-Branding-Fix: Tenants OHNE eigenes Logo bekommen
 *  NICHT mehr automatisch das Pilot-Logo (`assets/logo.png`) aufgedrückt —
 *  die `<img data-brand-logo>`-Elemente werden stattdessen versteckt
 *  (`hidden`-Attribut + `display:none`). Der Pilot-Tenant `rest_default`
 *  bekommt sein Logo via expliziter Settings-Migration zugewiesen
 *  (Migration 39, S48), sodass für ihn weiterhin das Grill & Green-Logo
 *  sichtbar bleibt. Demo-Tenants ohne hochgeladenes Logo zeigen einen
 *  Text-only Brand-Header — sauber für Memory `project_gg_firma_rollen`. */
export function applyBrandLogo() {
  try {
    if (typeof document === 'undefined') return;
    const snap = getBrandSnapshot();
    const els = document.querySelectorAll('img[data-brand-logo]');

    if (snap.isDefault) {
      // Kein Custom-Logo konfiguriert → Logo verstecken, KEIN Pilot-Fallback.
      els.forEach(el => {
        if (!el.hasAttribute('hidden')) el.setAttribute('hidden', '');
        if (el.style.display !== 'none') el.style.display = 'none';
      });
      return;
    }

    // Custom-Logo (oder explizit gesetzte Default-URL beim Pilot) → src setzen,
    // hidden entfernen.
    // S61 — Robust gegen Image-Load-Fehler: bei CSP-Block / unerreichbarer
    // externer URL fällt der Browser sonst auf den hässlichen Alt-Text zurück.
    // onerror-Handler aktiviert in dem Fall den brand-konsistenten Text-
    // Fallback (siehe .hero-logo-fallback in styles.css).
    const src = getBrandLogoSrc();
    els.forEach(el => {
      if (el.getAttribute('src') !== src) el.setAttribute('src', src);
      if (el.hasAttribute('hidden')) el.removeAttribute('hidden');
      if (el.style.display === 'none') el.style.display = '';
      // data-broken zurücksetzen, falls vorher ein Fail registriert war
      // und jetzt ein neues src probiert werden soll.
      if (el.dataset && el.dataset.broken) delete el.dataset.broken;
      // onerror-Hook idempotent setzen — falls applyBrandLogo nach App-Boot
      // mehrfach aufgerufen wird, registrieren wir den Listener trotzdem
      // nur einmal pro Element.
      if (!el.dataset || !el.dataset.brandLogoErrHook) {
        el.addEventListener('error', () => {
          el.dataset.broken = '1';
          el.style.display = 'none'; // inline-style schlägt CSS-Selektor
        });
        if (el.dataset) el.dataset.brandLogoErrHook = '1';
      }
    });
  } catch (_) { /* defensive — Branding ist UX, kein App-Blocker */ }
}

/** Pure-Helper: löst den aktuellen Restaurant-Namen auf. Resolver-Reihenfolge:
 *   1. `state.settings.restaurantName` (Parameter — bevorzugt für Re-Render
 *      nach Tenant-Wechsel, weil Caller frischen State übergeben kann).
 *   2. `state.restaurant.name` (Fallback, falls settings noch nicht geladen).
 *   3. `window.App.storage._getInternalState()` (für Aufrufer ohne state).
 *   4. 'Restaurant' (letzter Notnagel — niemals leerer Header).
 *
 *  Exportiert für Tests + Wiederverwendung in Edge-Cases. */
export function _resolveRestaurantName(state) {
  try {
    if (state && state.settings && typeof state.settings.restaurantName === 'string'
        && state.settings.restaurantName.trim()) {
      return state.settings.restaurantName.trim();
    }
    if (state && state.restaurant && typeof state.restaurant.name === 'string'
        && state.restaurant.name.trim()) {
      return state.restaurant.name.trim();
    }
    if (typeof window !== 'undefined' && window.App && window.App.storage) {
      const s = window.App.storage._getInternalState
        && window.App.storage._getInternalState();
      if (s && s.settings && typeof s.settings.restaurantName === 'string'
          && s.settings.restaurantName.trim()) {
        return s.settings.restaurantName.trim();
      }
      if (s && s.restaurant && typeof s.restaurant.name === 'string'
          && s.restaurant.name.trim()) {
        return s.restaurant.name.trim();
      }
    }
  } catch (_) { /* defensive */ }
  return 'Restaurant';
}

/** DOM-Apply — setzt den Restaurant-Namen auf alle Brand-Slots:
 *   - `#brand-name` (Admin-Cockpit Topbar)
 *   - `.brand-name-inline` (Customer-Form Body-Text)
 *
 *  Non-destruktiv: nur textContent-Update, keine HTML-Manipulation.
 *  Idempotent: schreibt nur bei Diff. Defensiv: never throws.
 *
 *  S48 Multi-Tenant-Branding-Fix: ersetzt die alten inline-Setter in
 *  `views/customer-form.js` (initCustomerForm) — durch zentrale Stelle
 *  wird der Brand-Name beim Tenant-Wechsel zuverlässig aktualisiert. */
export function applyBrandName(state) {
  try {
    if (typeof document === 'undefined') return;
    const name = _resolveRestaurantName(state);
    const el = document.getElementById('brand-name');
    if (el && el.textContent !== name) el.textContent = name;
    document.querySelectorAll('.brand-name-inline').forEach(node => {
      if (node.textContent !== name) node.textContent = name;
    });
  } catch (_) { /* defensive */ }
}

/** DOM-Apply — aktualisiert den Document-Title mit dem aktuellen Restaurant-
 *  Namen. Erhält den existierenden Suffix (z.B. ` – Reservierung` /
 *  ` – Admin-Cockpit`), falls einer im Title-Tag steht. Sonst Default
 *  ` – Reservierung`.
 *
 *  Beispiel: vorher "Grill & Green – Reservierung" → nachher
 *           "Demo Burger Laden 02 – Reservierung".
 *
 *  S48 Multi-Tenant-Branding-Fix: ohne diesen Apply würde der Browser-Tab
 *  weiterhin "Grill & Green – Reservierung" zeigen, selbst wenn ein
 *  Demo-Tester eingeloggt ist. Memory `project_gg_firma_rollen`. */
export function applyBrandTitle(state) {
  try {
    if (typeof document === 'undefined') return;
    const name = _resolveRestaurantName(state);
    const cur = document.title || '';
    const dashIdx = cur.indexOf(' – ');
    const suffix = dashIdx >= 0 ? cur.slice(dashIdx) : ' – Reservierung';
    const next = `${name}${suffix}`;
    if (cur !== next) document.title = next;
  } catch (_) { /* defensive */ }
}

/** Sammel-Aufruf — alle Brand-DOM-Slots in einem Rutsch aktualisieren.
 *  Wird vom Bootstrap (nach loadState + nach Auth-Restore) und vom
 *  auth:state-changed-Listener (Tenant-Wechsel) gerufen. Idempotent.
 *
 *  S48 Multi-Tenant-Branding-Fix: löst die Race-Condition aus S47, bei der
 *  `applyBrandLogo()` zu früh (vor _initFromSession) lief und nie wieder.
 *  Mit dieser Funktion + dem Listener-Aufruf im auth:state-changed-Pfad
 *  greift der Tenant-Wechsel zuverlässig auf alle Brand-Touchpoints durch.
 *
 *  state-Parameter ist optional — wenn weggelassen, fallen die internen
 *  Resolver auf `window.App.storage._getInternalState()` zurück. */
export function applyBrand(state) {
  applyBrandName(state);
  applyBrandLogo();
  applyBrandTitle(state);
}

// -----------------------------------------------------------------------------
// E-Mail-Logo-Embed (Phase 31, Session 29)
// -----------------------------------------------------------------------------
// Der Bon-Renderer setzt `<img src="<logoUrl>">` mit einer relativen URL
// (assets/logo.png) für die Web/Print-View. Eine relative URL funktioniert in
// E-Mail-Clients nicht — wir müssen vor dem Versand entweder eine absolute
// URL oder eine Base64-data-URL einbauen.
//
// Strategie:
//   - Storage-URL (https://…) → bleibt unverändert, Mail-Client lädt direkt.
//   - Default-Asset (relativ) → einmalig in Data-URL umwandeln und HTML
//     per String-Replace patchen.
//
// Wir cachen das Data-URL-Ergebnis im Modul-Scope, damit wiederholte
// Mail-Versände nicht jedes Mal fetchen.

let _emailLogoDataUrlCache = null;

async function _fetchAsDataUrl(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Logo fetch failed: ${res.status}`);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('FileReader error'));
    reader.readAsDataURL(blob);
  });
}

/** Liefert eine E-Mail-taugliche Logo-URL: Storage-URLs bleiben absolut,
 *  Default-Assets werden in eine `data:image/png;base64,…`-URL gewandelt
 *  und gecached. */
export async function getBrandLogoUrlForEmail() {
  const src = getBrandLogoSrc();
  if (/^https?:\/\//i.test(src) || /^data:/i.test(src)) return src;

  // S58 Block I.5 (Hard-Way): wenn die gespeicherte logoUrl relativ ist
  // (z.B. "assets/logo.png" — tenant-Setup-Bug), versuchen wir die
  // Tenant-publicUrl als Base zu nutzen. Damit funktioniert das Logo in
  // Mails auch dann, wenn das fetch-as-data-URL-Caching unten fehlschlägt.
  let tenantPublicUrl = '';
  try {
    if (typeof window !== 'undefined' && window.App && window.App.storage) {
      const s = window.App.storage._getInternalState
        && window.App.storage._getInternalState();
      const pUrl = s && s.settings && (s.settings.publicUrl || s.settings.public_url);
      if (typeof pUrl === 'string' && pUrl.trim()) tenantPublicUrl = pUrl.trim();
    }
  } catch (_) { /* defensive */ }

  if (_emailLogoDataUrlCache) return _emailLogoDataUrlCache;

  try {
    _emailLogoDataUrlCache = await _fetchAsDataUrl(src);
    return _emailLogoDataUrlCache;
  } catch (e) {
    // Fallback 1: absolute URL via Tenant-publicUrl (Hard-Way I.5).
    const absViaTenant = normalizeLogoUrl(src, tenantPublicUrl);
    if (absViaTenant && /^https?:\/\//i.test(absViaTenant)) return absViaTenant;
    // Fallback 2: absolute URL bauen — funktioniert wenigstens, wenn die
    // Mail im selben Browser-Origin geöffnet wird (z.B. Webmail-Tab).
    try {
      if (typeof window !== 'undefined' && window.location && window.location.href) {
        return new URL(src, window.location.href).toString();
      }
    } catch (_) { /* ignore */ }
    return src; // Letzter Fallback: relative URL bleibt — Mail-Client zeigt Broken-Image
  }
}

/** Baut ein E-Mail-taugliches HTML-Layout um einen Plain-Text-Body herum:
 *  Logo-Header oben, Restaurant-Name, dann der Text-Body in einer Card.
 *  Wird von sendConfirmationEmail/sendDeclineEmail/sendOrderEmail etc.
 *  aufgerufen, um die Standard-Templates brand-konform zu rendern.
 *
 *  Logo: bei Default-Asset → Base64-data-URL (via inlineBrandLogoForEmail
 *  über das vollständige HTML). Storage-URLs bleiben absolut.
 *
 *  Text-Escape: HTML-Spezialzeichen im Body werden escaped, Zeilenumbrüche
 *  bleiben als <br> erhalten (Resend-Default verschluckt sonst die Struktur).
 */
/** Wandelt Plain-Text-URLs in einem bereits HTML-escapeten Body in klickbare
 *  <a href>-Anker um. Wird VOR dem <br>-Umbruch aufgerufen — Pattern stoppt
 *  bei whitespace und HTML-Token-Starts. Bug-Fix S45 Block B: ohne diesen
 *  Schritt rendern Mail-Clients (Resend-HTML-Mode) URLs in HTML-Mails NICHT
 *  automatisch — Empfänger sah z.B. "Falls du stornieren möchtest: " mit
 *  Plain-URL dahinter, die nicht klickbar wirkte. Exportiert für Tests &
 *  Wiederverwendung (Edge-Function nutzt identische Logik in wrapBrand()). */
/**
 * S56-Hotfix-5: Akzeptiert optional eine Link-Color (Brand-Akzent) statt
 * hardcoded #2f6b3a. Ohne Color → Backward-Compat-Default greift.
 */
export function _autoLinkUrlsInEscapedHtml(escapedBody, linkColor) {
  if (typeof escapedBody !== 'string' || escapedBody.length === 0) return escapedBody || '';
  // S56 Hotfix 7: Default = Akzent-Orange (#d97a2c) für CTA-Pop auf dunklem
  // Hintergrund. Caller können primaryColor (Grün) übergeben für Subtilität.
  const color = (typeof linkColor === 'string' && linkColor.trim()) || '#d97a2c';
  return escapedBody.replace(
    /(https?:\/\/[^\s<>"']+)/g,
    `<a href="$1" style="color:${color};text-decoration:underline;font-weight:600;">$1</a>`
  );
}

/**
 * S56-Hotfix-5: Resolver für Brand-Settings im Mail-Render-Pfad.
 *
 * Lookup-Order:
 *   1. opts.brand (expliziter Override vom Caller — z.B. Edge-Function-Pfad)
 *   2. window.App.storage._getInternalState().restaurant.settings.brand
 *      (Frontend-Pfad, übliche Quelle)
 *   3. Defaults aus Pilot-Brand (zufällig identisch mit Hardcode-History).
 *
 * Liefert IMMER ein normalisiertes Brand-Objekt mit allen Feldern:
 *   { displayName, tagline, primaryColor, accentColor, logoText }
 *
 * Memory `feedback_gg_db_for_critical_data`: kritische Anzeige-Werte
 * sind Single-Source-of-Truth in restaurants.settings.brand, NICHT in
 * config.js-Defaults.
 */
export function _resolveMailBrand(opts) {
  opts = opts || {};
  let brand = {};
  let restName = opts.restaurantName;
  if (opts.brand && typeof opts.brand === 'object') {
    brand = opts.brand;
  } else {
    try {
      if (typeof window !== 'undefined' && window.App && window.App.storage
          && typeof window.App.storage._getInternalState === 'function') {
        const s = window.App.storage._getInternalState();
        // S56 Hotfix 5c: state-Struktur in storage.js Zeile 449:
        //   _state.settings = restaurant.settings  (FLACH, nicht unter .restaurant.settings)
        //   _state.restaurant = ... (nur für Tenant-Lookups, settings sind woanders)
        // Daher: brand-Block kommt aus state.settings.brand, NICHT state.restaurant.settings.brand.
        if (s && s.settings && s.settings.brand && typeof s.settings.brand === 'object') {
          brand = s.settings.brand;
        }
        if (!restName) {
          restName = (s && s.settings && s.settings.restaurantName)
            || (s && s.restaurant && s.restaurant.name);
        }
      }
    } catch (_) { /* defensive — Render darf nicht crashen */ }
  }
  const trimStr = (v) => (typeof v === 'string' && v.trim()) ? v.trim() : '';
  return {
    displayName:         trimStr(brand.displayName)         || trimStr(restName) || 'Grill & Green',
    tagline:             trimStr(brand.tagline)             || '',
    primaryColor:        trimStr(brand.primaryColor)        || '#2f6b3a',
    accentColor:         trimStr(brand.accentColor)         || '#d97a2c',
    logoText:            trimStr(brand.logoText)            || '',
    // S56 Hotfix 7: dunkler Mail-Look — Hintergrund/Card/Body-Text
    // werden aus der DB gelesen, mit Anthrazit-Defaults für „brand-konsistent
    // mit dem Logo".
    backgroundColor:     trimStr(brand.backgroundColor)     || '#1a1d1f',
    cardBackgroundColor: trimStr(brand.cardBackgroundColor) || '#22262a',
    bodyTextColor:       trimStr(brand.bodyTextColor)       || '#e8e6df'
  };
}

function _escapeXmlForMail(s) {
  return String(s || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export async function wrapBodyWithBrandHtml(textBody, opts) {
  opts = opts || {};
  // S56-Hotfix-5: brand-Settings aus DB statt hardcoded.
  const brand = _resolveMailBrand(opts);
  const displayNameSafe = _escapeXmlForMail(brand.displayName);
  const taglineSafe     = _escapeXmlForMail(brand.tagline);

  const escaped = (typeof textBody === 'string' ? textBody : '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  // URLs verlinken BEFORE <br>-Umbruch: das Pattern stoppt sauber bei whitespace
  // bzw. HTML-Token-Starts. Wenn nach Linking neue Zeilen kommen, kein Problem.
  // S56 Hotfix 7: Link-Color = accentColor (Orange) für Pop auf dunklem Hintergrund.
  const linked   = _autoLinkUrlsInEscapedHtml(escaped, brand.accentColor);
  let safeBody = linked.replace(/\n/g, '<br>');
  // S264 — Raw-Slots (Parität zur Edge-Function wrapBrand v53): [[KEY]] wird
  // NACH dem Escapen durch fertiges HTML ersetzt (Dienstplan-Tabelle). Der
  // Aufrufer escaped den Slot-Inhalt selbst.
  if (opts && opts.rawSlots && typeof opts.rawSlots === 'object') {
    for (const [k, v] of Object.entries(opts.rawSlots)) {
      safeBody = safeBody.split('[[' + k + ']]').join(String(v == null ? '' : v));
    }
  }

  // Logo-URL: erst auflösen (Storage-URL bleibt absolut, Default-Asset → data-URL)
  const logoUrl = await getBrandLogoUrlForEmail();
  const logoTag = logoUrl
    ? `<img src="${logoUrl}" alt="${brand.displayName.replace(/"/g, '&quot;')}" `
      + `style="display:block;margin:0 auto;max-width:140px;max-height:90px;object-fit:contain;">`
    : '';

  // S226 — optionale Footer-Zeilen (Parität zur Edge-Function wrapBrand v47):
  // Abo-CTA (transaktionale Gast-Mails) bzw. Pflicht-Abmeldung (werbliche
  // Custom-Mails). Im Builder-Preview zeigt opts.unsubscribeLink den fixen
  // Abmelde-Block ("gesetzlich vorgeschrieben"); Systemmail-Preview kann per
  // opts.newsletterLink den CTA zeigen. Gehen NICHT durch _autoLink (feste Anker).
  const nlLink = (opts && typeof opts.newsletterLink === 'string' && opts.newsletterLink.trim()) ? opts.newsletterLink.trim() : '';
  const unsubLink = (opts && typeof opts.unsubscribeLink === 'string' && opts.unsubscribeLink.trim()) ? opts.unsubscribeLink.trim() : '';
  const ctaRow = nlLink
    ? `        <tr><td style="padding:14px 32px 0 32px;text-align:center;font-size:13px;color:${brand.bodyTextColor};opacity:0.75;">
          Nichts verpassen — <a href="${nlLink}" style="color:${brand.accentColor};text-decoration:underline;font-weight:600;">unseren Newsletter abonnieren</a>
        </td></tr>\n`
    : '';
  const unsubRow = unsubLink
    ? `        <tr><td style="padding:14px 32px 0 32px;text-align:center;font-size:11px;color:${brand.bodyTextColor};opacity:0.55;line-height:1.5;">
          Du erhältst diese E-Mail, weil du unseren Newsletter abonniert hast. <a href="${unsubLink}" style="color:${brand.accentColor};text-decoration:underline;">Vom Newsletter abmelden</a>
        </td></tr>\n`
    : '';
  // S263 K2 — optionaler Button unter dem Body (Parität zur Edge-Function
  // wrapBrand v52, opts.ctaButton {href,label}; Dienstplan-Mails).
  const cta = (opts && opts.ctaButton && typeof opts.ctaButton.href === 'string' && opts.ctaButton.href.trim()) ? opts.ctaButton : null;
  const buttonRow = cta
    ? `        <tr><td style="padding:0 32px 26px 32px;text-align:center;">
          <a href="${cta.href.replace(/"/g, '&quot;')}" style="display:inline-block;background:${brand.accentColor};color:#ffffff;text-decoration:none;font-weight:700;font-size:15px;line-height:1.2;padding:13px 24px;border-radius:10px;">${_escapeXmlForMail(cta.label || 'Öffnen')}</a>
        </td></tr>\n`
    : '';

  // S56 Hotfix 7: dunkler Brand-Look — Hintergrund aus brand.backgroundColor,
  // Card aus brand.cardBackgroundColor, Body-Text aus brand.bodyTextColor.
  // Marken-Schriftfarbe = brand.primaryColor (leuchtet auf dunkel),
  // Akzent (Footer-Separator, sekundäre Highlights) = subtle.
  //
  // S225-HF (Mobil-Rendering, GMX-iOS): Fluid-Hybrid statt fixer 600px-Karte —
  // innere Tabelle width=100% + max-width:600px + margin:auto, plus <!--[if mso]-->
  // 600px-Wrapper NUR für Outlook (ignoriert max-width). Body-Zelle bricht lange
  // URLs (word-break/overflow-wrap). MUSS mit der Edge-Function `wrapBrand`
  // (process-mail-outbox v46) in Sync bleiben (Memory gg-edge-function-source-drift).
  return `<!DOCTYPE html>
<html lang="de"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="color-scheme" content="dark light"><meta name="supported-color-schemes" content="dark light"></head>
<body style="margin:0;padding:0;background:${brand.backgroundColor};font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:${brand.bodyTextColor};">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${brand.backgroundColor};padding:24px 12px;">
    <tr><td align="center">
      <!--[if mso]><table role="presentation" width="600" cellpadding="0" cellspacing="0"><tr><td><![endif]-->
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="width:100%;max-width:600px;margin:0 auto;background:${brand.cardBackgroundColor};border-radius:14px;box-shadow:0 2px 6px rgba(0,0,0,.25),0 8px 20px rgba(0,0,0,.18);overflow:hidden;">
        <tr><td style="padding:32px 32px 20px 32px;text-align:center;border-bottom:1px solid rgba(255,255,255,0.06);">
          ${logoTag}
          <div style="margin-top:12px;font-family:'Caveat',cursive,Georgia,serif;font-size:28px;color:${brand.primaryColor};font-weight:700;line-height:1.1;">
            ${displayNameSafe}
          </div>${taglineSafe ? `
          <div style="margin-top:6px;font-size:13px;color:${brand.primaryColor};opacity:0.85;letter-spacing:0.06em;">
            ${taglineSafe}
          </div>` : ''}
        </td></tr>
        <tr><td style="padding:28px 32px;font-size:15px;line-height:1.6;color:${brand.bodyTextColor};word-break:break-word;overflow-wrap:anywhere;">
          ${safeBody}
        </td></tr>
${buttonRow}${ctaRow}${unsubRow}        <tr><td style="padding:16px 32px 24px 32px;border-top:1px solid rgba(255,255,255,0.06);font-size:12px;color:${brand.bodyTextColor};opacity:0.55;text-align:center;letter-spacing:0.04em;">
          ${displayNameSafe} · Diese Mail wurde automatisch versendet.
        </td></tr>
      </table>
      <!--[if mso]></td></tr></table><![endif]-->
    </td></tr>
  </table>
</body></html>`;
}

/** Wie wrapBodyWithBrandHtml, aber für HTML-Bodies (z.B. Z-Bon-Receipt).
 *  Body wird NICHT escaped — Caller ist verantwortlich, dass das HTML
 *  sicher ist. Wenn ein komplettes <html>-Dokument übergeben wird, wird
 *  nur der <body>-Inhalt extrahiert.
 *
 *  Verwendet z.B. von sendZBonReceiptEmail, damit der Z-Bon im selben
 *  Brand-Layout wie die anderen Mails erscheint (Logo-Header, Footer). */
export async function wrapHtmlWithBrandHtml(innerHtml, opts) {
  opts = opts || {};
  // S56-Hotfix-5: brand-Settings aus DB statt hardcoded.
  const brand = _resolveMailBrand(opts);
  const displayNameSafe = _escapeXmlForMail(brand.displayName);
  const taglineSafe     = _escapeXmlForMail(brand.tagline);

  // Falls jemand ein komplettes Standalone-Dokument übergibt: nur Body extrahieren.
  let bodyHtml = String(innerHtml || '');
  const bodyMatch = bodyHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  if (bodyMatch) bodyHtml = bodyMatch[1];

  // S29 Funktionstest-Korrektur: das Brand-Wrap setzt selbst ein Logo oben.
  // Wenn der Body bereits ein <img class="receipt-logo"> mitbringt (z.B. aus
  // renderHeader), würde das Logo doppelt erscheinen. Entferne es deshalb hier.
  bodyHtml = bodyHtml.replace(/<img[^>]*class="receipt-logo"[^>]*>/gi, '');

  // Etwaiges inline CSS aus dem Standalone-Dokument übernehmen — damit die
  // Receipt-Klassen im gewrappten HTML noch greifen (.receipt-meta-grid etc.).
  let inlineCss = '';
  const cssMatches = String(innerHtml || '').matchAll(/<style[^>]*>([\s\S]*?)<\/style>/gi);
  for (const m of cssMatches) inlineCss += m[1] + '\n';

  const logoUrl = await getBrandLogoUrlForEmail();
  const logoTag = logoUrl
    ? `<img src="${logoUrl}" alt="${brand.displayName.replace(/"/g, '&quot;')}" `
      + `style="display:block;margin:0 auto;max-width:140px;max-height:90px;object-fit:contain;">`
    : '';

  // S56 Hotfix 7: dunkler Brand-Look (siehe wrapBodyWithBrandHtml).
  return `<!DOCTYPE html>
<html lang="de"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="color-scheme" content="dark light"><meta name="supported-color-schemes" content="dark light">
${inlineCss ? `<style>${inlineCss}</style>` : ''}
</head>
<body style="margin:0;padding:0;background:${brand.backgroundColor};font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:${brand.bodyTextColor};">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${brand.backgroundColor};padding:24px 12px;">
    <tr><td align="center">
      <!--[if mso]><table role="presentation" width="640" cellpadding="0" cellspacing="0"><tr><td><![endif]-->
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="width:100%;max-width:640px;margin:0 auto;background:${brand.cardBackgroundColor};border-radius:14px;box-shadow:0 2px 6px rgba(0,0,0,.25),0 8px 20px rgba(0,0,0,.18);overflow:hidden;">
        <tr><td style="padding:32px 32px 20px 32px;text-align:center;border-bottom:1px solid rgba(255,255,255,0.06);">
          ${logoTag}
          <div style="margin-top:12px;font-family:'Caveat',cursive,Georgia,serif;font-size:28px;color:${brand.primaryColor};font-weight:700;line-height:1.1;">${displayNameSafe}</div>${taglineSafe ? `
          <div style="margin-top:6px;font-size:13px;color:${brand.primaryColor};opacity:0.85;letter-spacing:0.06em;">${taglineSafe}</div>` : ''}
        </td></tr>
        <tr><td style="padding:24px 32px;word-break:break-word;overflow-wrap:anywhere;">
          ${bodyHtml}
        </td></tr>
        <tr><td style="padding:16px 32px 24px 32px;border-top:1px solid rgba(255,255,255,0.06);font-size:12px;color:${brand.bodyTextColor};opacity:0.55;text-align:center;letter-spacing:0.04em;">
          ${displayNameSafe} · Diese Mail wurde automatisch versendet.
        </td></tr>
      </table>
      <!--[if mso]></td></tr></table><![endif]-->
    </td></tr>
  </table>
</body></html>`;
}

/** Ersetzt im HTML-String relative Logo-Pfade durch E-Mail-taugliche URLs
 *  (Data-URL oder absolute Storage-URL). Idempotent. */
export async function inlineBrandLogoForEmail(html) {
  if (typeof html !== 'string' || !html) return html;

  const src = getBrandLogoSrc();
  if (/^https?:\/\//i.test(src) || /^data:/i.test(src)) return html;

  const replacement = await getBrandLogoUrlForEmail();
  if (replacement === src) return html;

  const escaped = src.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return html.replace(new RegExp(escaped, 'g'), replacement);
}

/** Read-Only-Snapshot des Brand-Objects (für Diagnostik / Settings-UI).
 *  Liefert `{ logoUrl, logoUploadedAt, isDefault }`. */
export function getBrandSnapshot() {
  try {
    if (typeof window !== 'undefined' && window.App && window.App.storage) {
      const s = window.App.storage._getInternalState
        && window.App.storage._getInternalState();
      const brand = (s && s.settings && s.settings.brand) || {};
      const hasCustom = typeof brand.logoUrl === 'string' && brand.logoUrl.trim();
      return {
        logoUrl: hasCustom ? brand.logoUrl.trim() : DEFAULT_LOGO_URL,
        logoUploadedAt: brand.logoUploadedAt || null,
        isDefault: !hasCustom
      };
    }
  } catch (_) { /* defensive */ }
  return { logoUrl: DEFAULT_LOGO_URL, logoUploadedAt: null, isDefault: true };
}
